import React, { useCallback, useEffect } from 'react'

import loadable from '@loadable/component'
import htmr from 'htmr'
import PropTypes from 'prop-types'
import { FaInfoCircle } from 'react-icons/fa'
import { useDispatch, useSelector } from 'react-redux'

import {
  campusEnums,
  hiddenFormParamEnums,
  queryStringParamEnums,
} from '../../enums'
import { useCampus, useHiddenFormParams, useProgram } from '../../hooks'
import useUserInfo from '../../hooks/useUserInfo'
import { formRedirectActions } from '../../redux/actions'
import { selectFormRedirect } from '../../redux/selectors'
import { getGAClientID } from '../../utils/cookie'
import ModalTwo from '../modals/ModalTwo'
import { Toggle } from '../modals/Toggle'

const FormSelectCampus = loadable(() => {
  return import('@ultimatemedical/acorn/dist/FormSelectCampus')
})

const FormNoCampus = ({
  className,
  FLOnlyMSG,
  includeUserErrorModal,
  submitButtonText,
  userErrorModalMessage,
}) => {
  const { campus } = useCampus()
  const dispatch = useDispatch()
  const { program } = useProgram()
  const { hiddenFormParams, mergeHiddenFormParams } = useHiddenFormParams()
  const formRedirect = useSelector(selectFormRedirect)
  const { setUserInfo } = useUserInfo()

  const leadIdCallback = useCallback(
    (token) => {
      mergeHiddenFormParams([
        {
          default: token,
          key: hiddenFormParamEnums.keys.LEAD_ID,
        },
      ])
      formRedirect.searchParams.append(queryStringParamEnums.uma.LEAD_ID, token)
      dispatch(formRedirectActions.update(formRedirect))
    },
    [dispatch, formRedirect, mergeHiddenFormParams],
  )

  // set the google clientID
  useEffect(() => {
    const clientID = getGAClientID()
    if (clientID) {
      mergeHiddenFormParams([
        {
          default: clientID,
          key: hiddenFormParamEnums.keys.ClientID,
        },
      ])
    }
  }, [mergeHiddenFormParams])

  const renderForm = (toggle = () => null) => (
    <FormSelectCampus
      beforeSubmitCallback={(values) => {
        setUserInfo({
          email: values.Email,
          firstName: values.FirstName,
          lastName: values.LastName,
          middleName: '',
          phoneNumber: values.Phone,
          stateCode: values.State,
        })
      }}
      FLOnly={campus === campusEnums.codes.CLEARWATER}
      FLOnlyMSG={FLOnlyMSG}
      key={JSON.stringify({ Campus: campus })}
      programCode={program.CODE}
      renderUserErrorModal={toggle}
      submitButtonText={
        submitButtonText ||
        (() => (
          <>
            Request Info&nbsp;
            <span className="spinner">&gt;</span>
          </>
        ))()
      }
      className={className}
      formParams={hiddenFormParams}
      redirect={formRedirect.toString()}
      removeFormParamsAndSpecifyDefaultValues={{
        Campus: campus,
        ProgramOnline: program.CODE,
      }}
      leadIdCallback={leadIdCallback}
    />
  )

  const renderFormWithToggleWrapper = () => (
    <Toggle>
      {({ on, toggle }) => (
        <>
          {renderForm(toggle)}
          {on && (
            <ModalTwo closeModal={toggle}>
              <FaInfoCircle className="modal-two-info-circle" />
              <span>{htmr(userErrorModalMessage)}</span>
            </ModalTwo>
          )}
        </>
      )}
    </Toggle>
  )

  return includeUserErrorModal ? renderFormWithToggleWrapper() : renderForm()
}

FormNoCampus.propTypes = {
  includeUserErrorModal: PropTypes.bool,
  userErrorModalMessage: PropTypes.string,
}

FormNoCampus.defaultProps = {
  includeUserErrorModal: false,
  userErrorModalMessage:
    "Students enrolled at the Clearwater Campus must be located in Florida. <a href='/campuses/online/'>Explore our online programs</a>.",
}

export default FormNoCampus
